import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, CheckCircle, Circle, ListChecks } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import BottomNav from '../components/BottomNav';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TutorialPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const gameName = searchParams.get('game') || 'Catan';
  const [sections, setSections] = useState([]);
  const [completedSections, setCompletedSections] = useState([]);
  const [expandedSection, setExpandedSection] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTutorial();
    fetchProgress();
  }, [gameName]);

  const fetchTutorial = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/tutorials`, { params: { game_name: gameName }, withCredentials: true });
      setSections(Array.isArray(data.sections) ? data.sections : []);
    } catch (err) { toast.error('Failed to load tutorial'); }
    finally { setLoading(false); }
  };

  const fetchProgress = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/tutorials/progress`, { params: { game_name: gameName }, withCredentials: true });
      setCompletedSections(Array.isArray(data.completed_sections) ? data.completed_sections : []);
    } catch (err) { /* silent */ }
  };

  const toggleComplete = async (section) => {
    const isCompleted = completedSections.includes(section);
    try {
      await axios.post(`${API_URL}/api/tutorials/progress`, {
        game_name: gameName, section, completed: !isCompleted
      }, { withCredentials: true });

      setCompletedSections(prev =>
        isCompleted ? prev.filter(s => s !== section) : [...prev, section]
      );
      if (!isCompleted) toast.success('Section completed!');
    } catch (err) { toast.error('Failed to update progress'); }
  };

  const progress = sections.length > 0 ? Math.round((completedSections.length / sections.length) * 100) : 0;

  return (
    <div className="app-container" data-testid="tutorial-page">
      <header className="p-6 pb-3">
        <div className="flex items-center gap-3 mb-4">
          <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-full bg-[#181824] flex items-center justify-center">
            <ArrowLeft size={20} weight="bold" className="text-white" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
              <ListChecks size={24} weight="duotone" className="text-[#39FF14]" />
              Learn {gameName}
            </h1>
            <p className="text-[#71717A] text-xs">Interactive tutorial</p>
          </div>
        </div>

        {/* Progress */}
        <div className="p-3 rounded-xl bg-[#0F0F16] border border-white/5 mb-4">
          <div className="flex justify-between items-center mb-2">
            <span className="text-[#A1A1AA] text-xs">Progress</span>
            <span className="text-white text-sm font-bold">{completedSections.length}/{sections.length}</span>
          </div>
          <div className="h-2 bg-[#181824] rounded-full overflow-hidden">
            <motion.div animate={{ width: `${progress}%` }} transition={{ duration: 0.5 }}
              className="h-full bg-gradient-to-r from-[#39FF14] to-[#00F0FF] rounded-full" />
          </div>
          {progress === 100 && <p className="text-[#39FF14] text-xs mt-2 text-center font-medium">Tutorial Complete!</p>}
        </div>
      </header>

      <div className="px-6 pb-32 space-y-2">
        {loading ? (
          <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-16 bg-[#181824] rounded-xl animate-pulse" />)}</div>
        ) : (
          sections.map((s, i) => {
            const isComplete = completedSections.includes(s.section);
            const isExpanded = expandedSection === s.section;
            return (
              <motion.div key={s.section} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                className={`rounded-xl border transition-all ${isComplete ? 'bg-[#39FF14]/5 border-[#39FF14]/20' : 'bg-[#0F0F16] border-white/5'}`}>
                <button onClick={() => setExpandedSection(isExpanded ? null : s.section)}
                  className="w-full p-4 flex items-center gap-3 text-left" data-testid={`tutorial-section-${s.section}`}>
                  {isComplete ? (
                    <CheckCircle size={24} weight="fill" className="text-[#39FF14] shrink-0" />
                  ) : (
                    <Circle size={24} weight="regular" className="text-[#71717A] shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-sm font-medium ${isComplete ? 'text-[#39FF14]' : 'text-white'}`}>{s.title}</h3>
                  </div>
                  <span className="text-[#71717A] text-xs">{i + 1}/{sections.length}</span>
                </button>

                {isExpanded && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
                    className="px-4 pb-4">
                    <div className="pl-9">
                      <p className="text-[#A1A1AA] text-xs leading-relaxed mb-3">{s.content}</p>
                      <button onClick={() => toggleComplete(s.section)}
                        className={`px-4 py-2 rounded-lg text-xs font-medium transition-all ${
                          isComplete ? 'bg-[#181824] text-[#71717A]' : 'bg-gradient-to-br from-[#39FF14]/20 to-[#00F0FF]/20 text-[#39FF14] border border-[#39FF14]/20'
                        }`} data-testid={`toggle-complete-${s.section}`}>
                        {isComplete ? 'Mark Incomplete' : 'Mark Complete'}
                      </button>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            );
          })
        )}
      </div>

      <BottomNav />
    </div>
  );
}
